import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { RootState } from "../store";
import {
  getOrganizationIds,
  hasOrganizationAccess,
  isAdminUser,
  isFullAdmin,
} from "../utils/organizationMembership";

export interface OrganizationMembership {
  /** False until the ID token has been read into Redux; the flags below are all false before then. */
  isResolved: boolean;
  groups: string[];
  /** In an organization or an admin: may use the Tools pages. */
  hasOrganizationAccess: boolean;
  /** Admin or AdminReadOnly. */
  isAdminUser: boolean;
  /** Admin only — exempt from per-user limits and able to write. */
  isFullAdmin: boolean;
  organizationIds: string[];
}

interface UserDetailsShape {
  groups?: string[];
  email?: string;
  username?: string;
}

// Redux glue over the pure helpers in utils/organizationMembership. Groups come from the Cognito ID token that VerticalNav stores on mount; a user with no groups at all has `groups` undefined even after that, so "resolved" is read from the token's other claims.
export const useOrganizationMembership = (): OrganizationMembership => {
  const details = useSelector(
    (state: RootState) => (state.user as { details?: UserDetailsShape })?.details
  );

  return useMemo(() => {
    const groups = details?.groups;
    return {
      isResolved: Boolean(details?.email || details?.username || groups),
      groups: groups ?? [],
      hasOrganizationAccess: hasOrganizationAccess(groups),
      isAdminUser: isAdminUser(groups),
      isFullAdmin: isFullAdmin(groups),
      organizationIds: getOrganizationIds(groups),
    };
  }, [details]);
};

export default useOrganizationMembership;
